import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Select, Option, Modal, ModalDialog } from "@mui/joy";
import { Add } from "@mui/icons-material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFetch, postFetch } from "../../../../fetch/fetch";
import styles from "./faqadmin.module.css";
import { formatShortDateTime, normalizeQuestion, FEEDBACK_TYPE_LABEL, toastOptions } from "./faqutil";

/**
 * FAQ 관리 > ② 검토 큐 (목업 AdminReview) — faq_feedback(helpful 제외) + 당시 질문/매칭 항목
 *  - 필터: 미처리 N / 답 없음 / 다른 항목 / 내용 틀림 | 처리됨, 지점 셀렉트
 *  - 같은 질문(정규화)이 여러 번 들어오면 한 카드로 묶어 ×N (프론트 group). 액션은 묶음 전체에 적용
 *  - 액션: 답 없음 → [+ 새 FAQ 추가](질문·추천 카테고리 프리필 드로어) / [무시]
 *          내용 틀림 → [FAQ #N 수정] / [현재 내용이 맞음](resolved) / [무시]
 *          다른 항목 → [FAQ #N 수정] / [새 FAQ 추가] / [무시]
 *  - 처리됨 탭: 처리자·메모·처리 결과 표시
 */
const typeBadgeClass : { [k : string] : string } = { no_answer : styles.badgeRed, wrong_item : styles.badgeBlue, wrong_content : styles.badgeAmber };

const FaqReview : React.FC<any> = (props : any) => {

    const [status, setStatus] = useState<"open" | "done">("open");
    const [type, setType] = useState<string>("");
    const [branch, setBranch] = useState<string>("");
    const [rows, setRows] = useState<any[]>([]);
    const [counts, setCounts] = useState<any>({ total : 0, no_answer : 0, wrong_item : 0, wrong_content : 0 });
    const [branches, setBranches] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState<{ [k : string] : boolean }>({});
    const [dismiss, setDismiss] = useState<{ open : boolean, ids : number[], reason : string, label? : string }>({ open : false, ids : [], reason : "" });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set("status", status);
            if (type) params.set("type", type);
            if (branch) params.set("branch", branch);
            const result : any = await getFetch(`/api/faq/review?${params.toString()}`);
            if (result && result.message === "success") {
                setRows(result.rows);
                setCounts(result.counts);
                setBranches(result.branches);
            }
        } catch (e) {
            console.log(e);
        } finally {
            setLoading(false);
        }
    }, [status, type, branch]);

    useEffect(() => {
        load();
    }, [load, props.refreshKey]);

    // 미처리는 같은 질문(정규화)+같은 유형을 묶는다. 처리됨은 묶지 않음
    const groups = useMemo(() => {
        if (status === "done") {
            return rows.map((r) => ({ key : String(r.id), main : r, all : [r] }));
        }
        const map : { [k : string] : { key : string, main : any, all : any[] } } = {};
        const order : string[] = [];
        for (const r of rows) {
            const k = `${r.type}|${normalizeQuestion(r.question)}`;
            if (!map[k]) {
                map[k] = { key : k, main : r, all : [] };
                order.push(k);
            }
            map[k].all.push(r);
        }
        return order.map((k) => map[k]);
    }, [rows, status]);

    const resolve = async (ids : number[], st : "resolved" | "dismissed", note : string, resolvedItemId? : number) => {
        try {
            for (const id of ids) {
                await postFetch(`/api/faq/review/${id}/resolve`, { status : st, resolutionNote : note, resolvedItemId : resolvedItemId || null });
            }
            toast.success(st === "dismissed" ? "무시 처리됨" : "처리됨", toastOptions);
            props.onChanged();
        } catch (e) {
            console.log(e);
            toast.error("처리에 실패했습니다", toastOptions);
        }
    };

    const toggle = (k : string) => setExpanded((p) => ({ ...p, [k] : !p[k] }));

    const renderMatched = (g : any) => {
        const r = g.main;
        const list : any[] = r.matched && r.matched.length > 0 ? r.matched : (r.item ? [{ id : r.item_id, role : "primary", item : r.item }] : []);
        if (list.length === 0) {
            return null;
        }
        return (
            <div className={styles.matched}>
                {list.map((m : any) => {
                    const k = `${g.key}-${m.id}`;
                    const isTarget = r.item_id && +r.item_id === +m.id;
                    return (
                        <div key={m.id}>
                            <div className={styles.matchedRow}>
                                <span className={styles.cellMuted}>매칭 #{m.id}{m.role === "primary" ? "" : " (함께 보기)"}{isTarget ? " · 피드백 대상" : ""}</span>
                                <span>{m.item ? m.item.question : "(삭제된 항목)"}</span>
                                {m.item ? <button className={styles.linkBtn} style={{ fontSize : "12px", padding : 0 }} onClick={() => toggle(k)}>{expanded[k] ? "답변 접기" : "답변 펼치기"}</button> : null}
                            </div>
                            {expanded[k] && m.item ? <div className={styles.matchedAnswer}>{m.item.answer}</div> : null}
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderActions = (g : any) => {
        const r = g.main;
        const ids : number[] = g.all.map((x : any) => x.id);
        const prefill = { question : r.question, category : r.suggested_category || (r.item ? r.item.category : "") };
        const editTarget = r.item_id || (r.matched && r.matched.length > 0 ? r.matched[0].id : null);
        if (r.type === "no_answer") {
            return (
                <div className={styles.actions}>
                    <button className={`${styles.btn} ${styles.btnMd} ${styles.btnPrimary}`} onClick={() => props.onNew(prefill, ids)}><Add sx={{ fontSize : 16 }} /> 새 FAQ 추가</button>
                    {editTarget ? <button className={`${styles.btn} ${styles.btnMd}`} onClick={() => props.onEdit(editTarget, ids)}>FAQ #{editTarget} 수정</button> : null}
                    <button className={`${styles.btn} ${styles.btnMd} ${styles.btnMuted}`} onClick={() => setDismiss({ open : true, ids, reason : "", label : r.question })}>무시</button>
                </div>
            );
        }
        if (r.type === "wrong_content") {
            return (
                <div className={styles.actions}>
                    {editTarget ? <button className={`${styles.btn} ${styles.btnMd} ${styles.btnPrimary}`} onClick={() => props.onEdit(editTarget, ids)}>FAQ #{editTarget} 수정</button> : null}
                    <button className={`${styles.btn} ${styles.btnMd}`} onClick={() => resolve(ids, "resolved", "현재 내용이 맞음", editTarget || undefined)}>현재 내용이 맞음</button>
                    <button className={`${styles.btn} ${styles.btnMd} ${styles.btnMuted}`} onClick={() => setDismiss({ open : true, ids, reason : "", label : r.question })}>무시</button>
                </div>
            );
        }
        return (
            <div className={styles.actions}>
                {editTarget ? <button className={`${styles.btn} ${styles.btnMd} ${styles.btnPrimary}`} onClick={() => props.onEdit(editTarget, ids)}>FAQ #{editTarget} 질문 문구 보강</button> : null}
                <button className={`${styles.btn} ${styles.btnMd}`} onClick={() => props.onNew(prefill, ids)}>새 FAQ 추가</button>
                <button className={`${styles.btn} ${styles.btnMd} ${styles.btnMuted}`} onClick={() => setDismiss({ open : true, ids, reason : "", label : r.question })}>무시</button>
            </div>
        );
    };

    return (
        <div className={styles.section}>
            <div className={styles.toolbar} style={{ gap : "8px" }}>
                <button className={`${styles.pill} ${status === "open" && !type ? styles.pillActive : ""}`} onClick={() => { setStatus("open"); setType(""); }}>미처리 <span className={styles.countRed}>{counts.total}</span></button>
                {(["no_answer", "wrong_item", "wrong_content"] as string[]).map((t) => (
                    <button key={t} className={`${styles.pill} ${status === "open" && type === t ? styles.pillActive : ""}`} onClick={() => { setStatus("open"); setType(t); }}>
                        {FEEDBACK_TYPE_LABEL[t]} {counts[t] > 0 ? <span className={styles.countRed}>{counts[t]}</span> : null}
                    </button>
                ))}
                <div className={styles.vdivider} />
                <button className={`${styles.pill} ${status === "done" ? styles.pillActive : ""}`} onClick={() => { setStatus("done"); setType(""); }}>처리됨</button>
                <Select size="sm" value={branch} onChange={(e, v) => setBranch(v === null ? "" : String(v))} sx={{ marginLeft : "auto", minWidth : "140px", borderRadius : "8px" }} placeholder="전체 지점">
                    <Option value="">전체 지점</Option>
                    {branches.map((b) => <Option key={b} value={b}>{b}</Option>)}
                </Select>
            </div>

            {
                loading && rows.length === 0 ? <div className={styles.empty}>불러오는 중…</div>
                : groups.length === 0 ? <div className={styles.empty}>{status === "open" ? "미처리 피드백이 없습니다 🎉" : "처리된 항목이 없습니다"}</div>
                : groups.map((g) => {
                    const r = g.main;
                    return (
                        <div key={g.key} className={styles.reviewCard}>
                            <div className={styles.reviewMeta}>
                                <span className={`${styles.badge} ${typeBadgeClass[r.type] || styles.badgeGray}`}>{FEEDBACK_TYPE_LABEL[r.type] || r.type}</span>
                                {g.all.length > 1 ? <span className={styles.countChip}>×{g.all.length}</span> : null}
                                {r.auto ? <span className={`${styles.badge} ${styles.badgeGray} ${styles.badgeSm}`}>자동</span> : null}
                                <span>{formatShortDateTime(r.asked_at)}</span>
                                <span>·</span>
                                <span>{r.user_name || "-"}{r.branch ? ` (${r.branch})` : ""}</span>
                                {r.suggested_category ? <><span>·</span><span>추천 카테고리: {r.suggested_category}</span></> : null}
                                {status === "done" ? <><span>·</span><span className={`${styles.badge} ${r.status === "resolved" ? styles.badgeGreen : styles.badgeGray} ${styles.badgeSm}`}>{r.status === "resolved" ? "처리됨" : "무시됨"}</span><span>{formatShortDateTime(r.resolved_at)} {r.resolved_by || ""}{r.resolution_note ? ` · ${r.resolution_note}` : ""}</span></> : null}
                            </div>
                            <div className={styles.reviewQ}>{r.question}</div>
                            {g.all.filter((x : any) => x.comment).map((x : any) => (
                                <div key={x.id} className={styles.comment}>직원 코멘트: {x.comment}</div>
                            ))}
                            {renderMatched(g)}
                            {status === "open" ? renderActions(g) : null}
                        </div>
                    );
                })
            }
            <div className={styles.footerRow}>
                <span>{status === "open" ? `미처리 ${counts.total}건 중 ${rows.length}건 표시 (최근 500건)` : `처리됨 ${rows.length}건 표시 (최근 500건)`}</span>
            </div>

            <Modal open={dismiss.open} onClose={() => setDismiss({ open : false, ids : [], reason : "" })}>
                <ModalDialog sx={{ padding : 0, borderRadius : "14px", width : "420px", maxWidth : "95vw" }}>
                    <div className={styles.modal} style={{ boxShadow : "none" }}>
                        <div className={styles.modalHeader}>무시 처리</div>
                        <div className={styles.modalBody}>
                            <div style={{ fontSize : "14px", lineHeight : 1.6 }}>{dismiss.label}</div>
                            <div className={styles.field}>
                                <div className={styles.label}>사유 <span>(선택)</span></div>
                                <input className={styles.input} value={dismiss.reason} onChange={(e) => setDismiss((d) => ({ ...d, reason : e.target.value }))} placeholder="예: FAQ 범위 밖 질문" />
                            </div>
                            {dismiss.ids.length > 1 ? <div className={styles.helper}>묶인 {dismiss.ids.length}건이 함께 무시 처리됩니다.</div> : null}
                        </div>
                        <div className={styles.modalFooter}>
                            <button className={styles.btn} onClick={() => setDismiss({ open : false, ids : [], reason : "" })}>취소</button>
                            <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={async () => { await resolve(dismiss.ids, "dismissed", dismiss.reason.trim()); setDismiss({ open : false, ids : [], reason : "" }); }}>무시</button>
                        </div>
                    </div>
                </ModalDialog>
            </Modal>
        </div>
    );
};

export default FaqReview;
